import { CabButton } from "@CabComponents/CabButton";
import { Alert, Box, Link, FormControl, Typography, useMediaQuery } from "@mui/material";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import colors from "../../colors";
import CabinetPage from "../../components/Common/CabinetPage";
import PollResponseCTA from "../../components/Meeting/PollResponseCTA/PollResponseCTA";
import { EVENT_TYPE, PAGE_URL } from "../../constants";
import theme from "../../theme";
import { 
  Calendar, ExternalMeetingInfo, Leader, MeetingPollPriority, MeetingQuestionAnswer,
  MeetingQuestionAnswerSubmission, Meetings, MeetingSlot, MeetingStatus, Organization, 
  ParticipantsWithSelections, SelectedSlots,
} from "../../store";
import { successConfetti } from "../../utils/renderUtils";
import { getNumResponses, humanReadableDuration, TimeZone } from "../../utils/scheduleUtils";
import AttendeeInfoForm from "./AttendeeInfoForm";
import PollSelectionTable from "./PollSelectionTable";
import QuestionResponse from "./QuestionResponse";
import Success from "./Success";
// import { CabTooltip } from "@CabComponents/CabTooltip";
import { CabModal } from "@CabComponents/CabModal";
import { Controller, useForm } from "react-hook-form";
import { CabTextInput } from "@CabComponents/CabTextInput";
import CabSpinner from "@CabComponents/CabSpinner";
import { Link as RouterLink } from 'react-router';
import { getEmailHash } from "../../utils/authUtils";
import BookMeetingWrapper from "../BookMeeting/BookMeetingWrapper";
import { MeetingBookingStep } from "../BookMeeting/BookMeeting";
import { SchedulingPreferences } from "../../store/cabinetApi/generated/scheduling";
import { IoTimeOutline } from "react-icons/io5";
import { CabIcon } from "@CabComponents/CabIcon";


export type PageProps = {
  loading: boolean;
  pollInfo?: PollInfo;
  isAuthenticated: boolean;
  onEdit: (emails: string[]) => void;
  poll: ExternalMeetingInfo | undefined
  onUserInfoSubmit: (emails: string[]) => void;
  onSubmit: (submissions: Array<PollSubmission>) => Promise<void>;
  defaultSlots: ParticipantSelectedSlots;
  logo: string;
  returningUserEmailHashes: {[hash: string]: string};
  organization: Organization;
  fetchMeetingQuestionAnswers: (emails: string[])
  => Promise<{ email: string; answers: {[id: number]: MeetingQuestionAnswer} }[]>;
  calendarTimezoneSelected?: TimeZone;
  onCalendarTimezoneSelected: (tz: TimeZone) => void;
  date: { start: DateTime, end: DateTime };
  onDateChange?: (start: DateTime, end: DateTime) => void;
  userData?: {
    leaders: Leader[];
    meetingSlots: MeetingSlot[];
    recurringSlots: MeetingSlot[];
    meetings: Meetings;
    calendars: Calendar[];
    schedulingPrefs?: SchedulingPreferences;
    selectedLeader: Leader | null,
    onSelectedLeaderChange: (l: Leader | null) => void,
  };
  onLogin: () => void;
};

const PollVotingPage = ({
  loading, pollInfo, ...props
}: PageProps) => (
  <CabinetPage
    pageName={'Poll Voting'}
    noHeader
  >
    {pollInfo ? (
      <PollVoting pollInfo={pollInfo} loading={loading} {...props} />
    ) : (
      <Box
        hidden={!!pollInfo || loading}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: 5
        }}>
        <Alert severity="warning">
          No poll was found at this link
        </Alert>
      </Box>
    )}
    <Box
      hidden={!loading}
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center'
      }}>
      <CabSpinner scale={4} color='inherit'/>
    </Box>
  </CabinetPage>
);

export default PollVotingPage;


export type PollInfo = {
  from: string;
  name: string;
  description: string;
  durationMinutes: number;
  expectedParticipants: number;
  slots: { start: DateTime; end: DateTime }[];
  participants: ParticipantsWithSelections;
};

export type Attendee = {
  name: string;
  email: string;
  emailHash: string;
  noTimesComment: string | null;
  required?: boolean;
  voterType?: 'participant' | 'leader';
  meetingLeaderId?: number;
  leaderId?: number;
};

enum PollStep {
  PARTICIPANT_INFO = 0,
  SELECT_TIMES = 1,
  QUESTIONS = 2,
  CONFIRMATION = 3,
}

export interface PollSubmission {
  attendeeName: string,
  email: string,
  required?: boolean,
  selectedSlots: Map<number, SelectedSlots[number] | null>,
  meetingQuestionAnswers: MeetingQuestionAnswerSubmission[],
  noTimesWork: boolean,
  noTimesComment: string | null,
  voterType?: 'participant' | 'leader',
  meetingLeaderId?: number,
}

export interface ParticipantSelectedSlots {
  [email: string]: SelectedSlots;
}

export type Props = {
  pollInfo: PollInfo;
  loading: boolean;
  onSubmit: (submissions: Array<PollSubmission>) => Promise<void>;
  onUserInfoSubmit: (emails: string[]) => void;
  defaultSlots: ParticipantSelectedSlots;
  onEdit: (emails: string[]) => void;
  isAuthenticated: boolean;
  logo: string;
  returningUserEmailHashes: {[hash: string]: string};
  poll: ExternalMeetingInfo | undefined;
  fetchMeetingQuestionAnswers: (emails: string[])
  => Promise<{ email: string; answers: {[id: number]: MeetingQuestionAnswer} }[]>;
  organization: Organization;
  date: { start: DateTime, end: DateTime };
  onDateChange?: (start: DateTime, end: DateTime) => void;
  calendarTimezoneSelected?: TimeZone;
  onCalendarTimezoneSelected: (tz: TimeZone) => void;
  userData?: {
    leaders: Leader[];
    meetingSlots: MeetingSlot[];
    recurringSlots: MeetingSlot[];
    meetings: Meetings;
    calendars: Calendar[];
    schedulingPrefs?: SchedulingPreferences;
    selectedLeader: Leader | null,
    onSelectedLeaderChange: (l: Leader | null) => void,
  };
  onLogin: () => void;
};


const PollVoting = ({
  pollInfo: { from, name, description, durationMinutes, expectedParticipants, slots, participants },
  logo, onUserInfoSubmit, poll, defaultSlots, isAuthenticated, returningUserEmailHashes, 
  fetchMeetingQuestionAnswers, onSubmit, onEdit, organization, calendarTimezoneSelected, onCalendarTimezoneSelected,
  date, onDateChange, userData, onLogin, loading,
}: Props) => {
  const [step, setStep] = useState(PollStep.PARTICIPANT_INFO);
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [isStepValid, setIsStepValid] = useState(false);
  const [formSubmit, setFormSubmit] = useState<(() => void) | undefined>(undefined);
  const [/*isQuestionResponseValid*/, setQuestionResponseValid] = useState(false);
  const [selectedSlots, setSelectedSlots]
    = useState<{ [email: string]: Map<number, SelectedSlots[number] | null> }>({});
  const latestSelectedSlotsRef = useRef(selectedSlots);
  const [answers, setAnswers] = useState<{ [email: string]: MeetingQuestionAnswerSubmission[] }>({});
  const latestAnswersRef = useRef(answers);
  const [noTimesWork, setNoTimesWork] = useState(false);
  const [saving, setSaving] = useState(false);
  const [/*addedParticipants*/, setAddedParticipants] = useState<
    { name: string; email: string; first_response_date: string | null, no_times_comment: string | null }[]>([]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [noTimesDisabled, setNoTimesDisabled] = useState(true);
  const [noTimesHover, /*setNoTimesHover*/] = useState(false); 
  const [openNoTimesModal, setOpenNoTimesModal] = useState(false);
  const [openSlotNoteModal, setOpenSlotNoteModal] = useState(false);
  const [slotNoteModalMode, setSlotNoteModalMode] = useState<"add" | "edit">("add");
  const [slotNoteModalEmail, setSlotNoteModalEmail] = useState<string>("");
  const [slotNoteModalSlotIndex, setSlotNoteModalSlotIndex] = useState<number>(-1);
  const [slotNoteModalSlot, setSlotNoteModalSlot] = useState<SelectedSlots[number] | null>(null);
  const [slotNoteText, setSlotNoteText] = useState("");
  // const [slotsInitialized, setSlotsInitialized] = useState(false);

  const {control, getValues, reset, handleSubmit} = useForm<(string | null)[]>({defaultValues: []});

  useEffect(() => {
    const values = getValues();
    if (attendees.length > values.length) {
      const diff = attendees.length - values.length;
      
      reset([...values, ...Array(diff).fill(null)], {keepDirtyValues: true});
    } 
  }, [attendees, getValues, reset]);

  useEffect(() => {
    latestAnswersRef.current = answers;
  }, [answers]);

  useEffect(() => {
    latestSelectedSlotsRef.current = selectedSlots;
  }, [selectedSlots]);

  const selectedSlotsLength: {[email: string]: number} = useMemo(() => Object.fromEntries(
    Object.entries(selectedSlots).map(
      ([email, slotsItr]) => {
        const count = [...slotsItr.values()].filter(s => s?.priority).length;
        return [email, count];
      }
    )
  ), [selectedSlots]);

  const selectedCount = useMemo(
    () => Object.values(selectedSlotsLength || {}).reduce((prev, curr) => curr + prev, 0)
    ,
    [selectedSlotsLength]
  );


  const submitComments = (data: (string | null)[], curSelectedCount: number) => {
    const newAttendees = [...attendees];
    for (let i = 0; i < newAttendees.length; i++) {
      newAttendees[i].noTimesComment = data[i];
    }
    setAttendees(newAttendees);
    setNoTimesWork(true);
    setOpenNoTimesModal(false);
    handleNext({fromNoTimesWorkModal: true, curSelectedCount});
  };

  const questionsPresent = (poll?.questions?.length || 0) > 0;
  const participantAttendees = useMemo(
    () => attendees.filter(attendee => attendee.voterType !== 'leader'),
    [attendees]
  );
  const hasLeaderAttendees = attendees.some(attendee => attendee.voterType === 'leader');
  const questionsPresentForSelectedAttendees = questionsPresent && participantAttendees.length > 0;
  const durationText = humanReadableDuration(durationMinutes);

  const hidePanel = useMediaQuery(theme.breakpoints.down(1000));
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));

  const showCalendarButton = isAuthenticated && !calendarOpen;

  const convertedSlots = useMemo(() => slots.map((slot, i) => ({
    ...slot,
    converted: calendarTimezoneSelected ? {
      ...slot,
      start: slot.start.setZone(calendarTimezoneSelected.name),
      end: slot.end.setZone(calendarTimezoneSelected.name)
    } : slot,
    index: i,
  })), [slots, calendarTimezoneSelected]);
  
  // Initialize previously-saved selections for returning users, and keep temporary
  // client-side selection IDs in sync after the first save.
  useEffect(() => {
    if (loading) return;
    if (Object.keys(defaultSlots).length === 0) return;
    if (slots.length === 0) return;
    setSelectedSlots(prev => {
      const next = { ...prev };

      Object.keys(defaultSlots).forEach(participantEmail => {
        const existing = next[participantEmail];
        const isMissing = !existing;
        const isEmpty = existing ? Array.from(existing.values()).every(v => v == null) : false;
        if (isMissing || isEmpty) {
          const tempMap = new Map<number, SelectedSlots[number] | null>();
          slots.forEach((slot, i) => {
            const defaultSlot = defaultSlots[participantEmail].find(s =>
              s.start.equals(slot.start) && s.end.equals(slot.end),
            );
            tempMap.set(i, defaultSlot ?? null);
          });
          next[participantEmail] = tempMap;
          return;
        }

        const tempMap = new Map(existing);
        slots.forEach((slot, i) => {
          const selectedSlot = tempMap.get(i);
          const defaultSlot = defaultSlots[participantEmail].find(s =>
            s.start.equals(slot.start) && s.end.equals(slot.end),
          );
          if (!selectedSlot || !defaultSlot || selectedSlot.id > 0) return;

          tempMap.set(i, {
            ...selectedSlot,
            id: defaultSlot.id,
          });
        });
        next[participantEmail] = tempMap;
      });

      latestSelectedSlotsRef.current = next;
      return next;
    });
  }, [defaultSlots, loading, slots]);

  // update answers state with answers from returning users
  const handleFetchAnswers = useCallback(async () => {
    const emails = Object.values(returningUserEmailHashes);
    const curAnswers = await fetchMeetingQuestionAnswers(emails);
    const participantAnswers: { [email: string]: MeetingQuestionAnswerSubmission[] } = curAnswers
      .filter(({ answers: ans }) => ans)
      .map(({ email, answers: ans }) => ({
        [email]: poll?.questions?.map(question => {
          const answer = Object.values(ans).find(ansItr => ansItr.question === question.id);
          return answer
            ? {...answer, participant: undefined }
            : { id: -1, question: question.id, text: '' };
        }) || [],
      }))
      .reduce((a, b) => ({ ...a, ...b }), {});
    
    setAnswers(participantAnswers);
    // This was triggering way too often - we only really want it to trigger when the participant changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(returningUserEmailHashes)]);

  useEffect(() => {
    handleFetchAnswers();
  }, [handleFetchAnswers]);

  const handleViewCalendar = useCallback(() => {
    if (!isAuthenticated) {
      onLogin();
    } else {
      setCalendarOpen(true);
    }
  }, [isAuthenticated, onLogin, setCalendarOpen]);
  
  const handleCheckSlot = useCallback((email: string, value: number, priority: MeetingPollPriority | null) => {
    const newPriority = priority === null
      ? MeetingPollPriority.AVAILABLE
      : (priority === MeetingPollPriority.AVAILABLE )
        ? MeetingPollPriority.POTENTIAL
        : undefined;

    setSelectedSlots(prev => {
      const tempMap = new Map(prev[email]);
      const storeSlot = tempMap.get(value);

      if (storeSlot) {
        tempMap.set(value, {
          ...storeSlot,
          priority: newPriority,
          note: newPriority === undefined ? null : storeSlot.note ?? null,
        });
      } else {
        const slot = slots[value];
        if (slot) {
          // for new slots, we want the ID to be negative and never 0 or -1 (-1 conflicts with recurring slots)
          tempMap.set(value, {...slot, priority: newPriority, id: -(value + 2), note: null});
        }
      }
      const next = { ...prev, [email]: tempMap };
      latestSelectedSlotsRef.current = next;
      return next;
    });
    setNoTimesWork(false);
  }, [slots]);

  const updateLocalSlotNote = useCallback((email: string, slotIndex: number, note: string | null) => {
    setSelectedSlots(prev => {
      const curMap = prev[email] ? new Map(prev[email]) : new Map<number, SelectedSlots[number] | null>();
      const curSlot = curMap.get(slotIndex);
      if (curSlot) {
        curMap.set(slotIndex, { ...curSlot, note });
      }
      const next = { ...prev, [email]: curMap };
      latestSelectedSlotsRef.current = next;
      return next;
    });
  }, []);

  const handleOpenSlotNoteModal = useCallback((payload: {
    participantEmail: string;
    slotIndex: number;
    slot: SelectedSlots[number];
    mode: "add" | "edit";
  }) => {
    setSlotNoteModalMode(payload.mode);
    setSlotNoteModalEmail(payload.participantEmail);
    setSlotNoteModalSlotIndex(payload.slotIndex);
    setSlotNoteModalSlot(payload.slot);
    setSlotNoteText(payload.slot.note ?? "");
    setOpenSlotNoteModal(true);
  }, []);

  const handleRemoveSlotNote = useCallback((payload: {
    participantEmail: string;
    slotIndex: number;
    slot: SelectedSlots[number];
  }) => {
    updateLocalSlotNote(payload.participantEmail, payload.slotIndex, null);
  }, [updateLocalSlotNote]);

  const handleSaveSlotNote = useCallback(() => {
    if (!slotNoteModalSlot) return;
    const normalizedNote = slotNoteText.trim() ? slotNoteText.trim() : null;

    updateLocalSlotNote(slotNoteModalEmail, slotNoteModalSlotIndex, normalizedNote);
    setOpenSlotNoteModal(false);
  }, [
    slotNoteModalSlot,
    slotNoteModalEmail,
    slotNoteModalSlotIndex,
    slotNoteText,
    updateLocalSlotNote,
  ]);

  const setNextStep = useCallback(() => {
    setStep(step + 1);
  }, [step]);
  
  const handleNext = useCallback(async ({
    fromNoTimesWorkModal = false, curSelectedCount = 0
  }: {fromNoTimesWorkModal?: boolean, curSelectedCount?: number}) => {
    if (step === PollStep.SELECT_TIMES) {
      setCalendarOpen(false);
    }
    if (!fromNoTimesWorkModal && step === PollStep.SELECT_TIMES && curSelectedCount === 0 && !hasLeaderAttendees) {
      setOpenNoTimesModal(true);
    } else if (
      step === PollStep.QUESTIONS || (step === PollStep.SELECT_TIMES && !questionsPresentForSelectedAttendees)
    ) {
      // Wait for state updates to complete
      await new Promise(resolve => setTimeout(resolve, 0));
      
      const submission: PollSubmission[] = attendees.map(attendee => ({
        attendeeName: attendee.name, 
        email: attendee.email,
        required: attendee.required,
        selectedSlots: latestSelectedSlotsRef.current[attendee.email] || new Map(),
        meetingQuestionAnswers: latestAnswersRef.current[attendee.email] || [],
        noTimesComment: attendee.noTimesComment,
        noTimesWork,
        voterType: attendee.voterType,
        meetingLeaderId: attendee.meetingLeaderId,
      }));
      if (
        (fromNoTimesWorkModal && curSelectedCount === 0) || step === PollStep.QUESTIONS || curSelectedCount > 0
      ) {
        setSaving(true);
        await onSubmit(submission);
        setAddedParticipants([]);
        setSaving(false);
        setStep(PollStep.CONFIRMATION);
        successConfetti();
      } 
    } else {
      onUserInfoSubmit(attendees.map(a => a.email));
      setNextStep();
    }
  }, [
    attendees,
    noTimesWork,
    onSubmit,
    onUserInfoSubmit,
    hasLeaderAttendees,
    questionsPresentForSelectedAttendees,
    setNextStep,
    step,
  ]);
  
  const handleAttendeeFormStateChange = useCallback((isValid: boolean, submit: () => void) => {
    setIsStepValid(isValid);
    setFormSubmit(() => submit);
  }, []);

  const handleQuestionFormStateChange = useCallback((isValid: boolean, submit: () => void) => {
    setIsStepValid(isValid);
    setFormSubmit(() => async () => {
      submit();
      handleNext({
        fromNoTimesWorkModal: false,
        curSelectedCount: selectedCount,
      });
    });
  }, [handleNext, selectedCount]);

  const handleEditSelections = useCallback(() => {
    onEdit(attendees.map(a => a.email));
    setStep(PollStep.SELECT_TIMES);
  }, [attendees, onEdit]);
  
  const handleSubmitTimes = useCallback(() => {
    handleNext({ fromNoTimesWorkModal: false, curSelectedCount: selectedCount });
  }, [handleNext, selectedCount]);
  
  const fromString = poll?.meeting_organizer_override && poll?.meeting_organizer_override !== ''
    ?  poll.meeting_organizer_override 
    : `${poll?.create_user.first_name} ${poll?.create_user.last_name}`; 

  const handleSelectParticipants = useCallback((selectedParticipants: {
    name: string; email: string; required?: boolean; voterType?: 'participant' | 'leader';
    meetingLeaderId?: number; leaderId?: number;
  }[]) => {
    const newAttendees = selectedParticipants.map(participant => ({
      name: participant.name,
      email: participant.email,
      emailHash: getEmailHash(participant.email),
      noTimesComment: null,
      required: participant.required,
      voterType: participant.voterType,
      meetingLeaderId: participant.meetingLeaderId,
      leaderId: participant.leaderId,
    }));

    setAttendees(newAttendees);

    // `handleNext` reads `attendees` from closure; we need to submit the *new* emails immediately.
    onUserInfoSubmit(newAttendees.map(a => a.email));
    setStep(PollStep.SELECT_TIMES);
    // Note: default slot + note rehydration happens via `defaultSlots` and the initialization effect.
  }, [setAttendees, onUserInfoSubmit]);

  const numRespondedParticipants = useMemo(() => getNumResponses(participants), [participants]);
  const participantsWithoutActive = participants.filter(
    p => !attendees.find(a => (a.email && a.email === p.email) || (a.emailHash && a.emailHash === p.emailHash))
  );
  const showVoterNames = poll?.show_voter_names === true;
  const displayedParticipantsWithoutActive = useMemo(() => (
    showVoterNames
      ? participantsWithoutActive
      : participantsWithoutActive.map((participant, idx) => ({
        ...participant,
        name: `Attendee ${idx + 1}`,
        email: null,
      }))
  ), [participantsWithoutActive, showVoterNames]);
  const votingLeaderOptions = useMemo(() => (poll?.leader_info || [])
    .filter(leader => leader.allow_poll_voting)
    .map(leader => ({
      id: leader.meeting_leader_id,
      name: `${leader.first_name} ${leader.last_name}`.trim(),
      email: `leader:${leader.meeting_leader_id}`,
      emailHash: `leader:${leader.meeting_leader_id}`,
      selectedSlots: [],
      first_response_date: null,
      required: leader.required,
      voterType: 'leader' as const,
      meetingLeaderId: leader.meeting_leader_id,
      leaderId: leader.id,
    })), [poll?.leader_info]);
  const attendeeListEnabled = showVoterNames
    && (!!poll?.enable_public_attendee_list || votingLeaderOptions.length > 0);
  const selectableVoters = useMemo(() => {
    const votersByKey = new Map<string, ParticipantsWithSelections[number] & { email: string }>();

    if (showVoterNames && poll?.enable_public_attendee_list) {
      (participants as (ParticipantsWithSelections[number] & { email: string })[]).forEach(participant => {
        votersByKey.set(participant.emailHash || participant.email, participant);
      });
    }

    if (showVoterNames) {
      votingLeaderOptions.forEach(leader => {
        const existingLeader = votersByKey.get(leader.emailHash);
        votersByKey.set(leader.emailHash, {
          ...leader,
          ...existingLeader,
          email: leader.email,
          emailHash: leader.emailHash,
          voterType: leader.voterType,
          meetingLeaderId: leader.meetingLeaderId,
          leaderId: leader.leaderId,
          selectedSlots: existingLeader?.selectedSlots || leader.selectedSlots,
          first_response_date: existingLeader?.first_response_date || leader.first_response_date,
          required: existingLeader?.required ?? leader.required,
        });
      });
    }

    return Array.from(votersByKey.values());
  }, [participants, poll?.enable_public_attendee_list, showVoterNames, votingLeaderOptions]);

  const Step = useMemo(() => {
    return [
      <AttendeeInfoForm
        key={'attendeeInfoForm'}
        from={fromString}
        enablePublicAttendeeList={attendeeListEnabled}
        // participants who have been manually added or already responded
        participants={selectableVoters}
        defaultSelectedAttendees={attendees}
        onSelectParticipants={handleSelectParticipants}
        allowAddAttendees={poll?.enable_public_attendee_list && poll?.allow_add_participants}
        onFormStateChange={handleAttendeeFormStateChange}
      />,
      <PollSelectionTable
        key={'selectionTable'}
        activeParticipants={attendees}
        attendeesDisplayName={attendees[0]?.name || attendees[0]?.email || ''}
        handleCalendarTimezoneSelected={onCalendarTimezoneSelected}
        participants={displayedParticipantsWithoutActive}
        convertedSlots={convertedSlots}
        handleCheckSlot={handleCheckSlot}
        selectedSlots={selectedSlots}
        onOpenSlotNoteModal={handleOpenSlotNoteModal}
        onRemoveSlotNote={handleRemoveSlotNote}
        calendarTimezoneSelected={calendarTimezoneSelected}
        isMobile={hidePanel}
        name={name}
        description={description}
        durationText={durationText}
        participantLength={numRespondedParticipants}
        userData={userData}
        date={date}
        onDateChange={onDateChange}
        openCalendar={calendarOpen}
        onCloseCalendar={() => setCalendarOpen(false)}
        onEndReached={() => setNoTimesDisabled(false)}
        enhancePollTableCursors={noTimesHover}
        onSubmit={handleSubmitTimes}
        onFormStateChange={(isValid, submit) => {
          // setIsAttendeesValid(isValid);
          setFormSubmit(() => submit);
        }}
        showCalendarButton={showCalendarButton}
        onViewCalendar={handleViewCalendar}
        isAuthenticated={isAuthenticated}
        googleCalIds={userData?.calendars?.filter(c => 
          c.provider === 1 && userData?.selectedLeader && c.leaders?.includes(userData.selectedLeader.id)
        ).map(c => c.calendar_id) || []}
        msCalIds={userData?.calendars?.filter(c => 
          c.provider === 2 && userData?.selectedLeader && c.leaders?.includes(userData.selectedLeader.id)
        ).map(c => c.calendar_id) || []}
        startDate={date.start}
        endDate={date.end}
        forTimezone={calendarTimezoneSelected?.name || "UTC"}
        showCanceledDeclined={false}
        prefetchNextPeriod={true}
        allDaySlots={!!poll?.all_day_slots}
      />,
      <QuestionResponse
        key={'questionResponse'}
        poll={poll}
        participants={participantAttendees}
        defaultAnswers={answers}
        onSubmitAnswers={setAnswers}
        setQuestionResponseValid={setQuestionResponseValid}
        onFormStateChange={handleQuestionFormStateChange}
      />,
      <Success
        key={'success'}
        poll={poll}
        answers={answers}
        votedSlots={selectedSlots}
        participants={attendees}
        onEditSelections={handleEditSelections}
        allDaySlots={!!poll?.all_day_slots}
      />
    ][step];
  }, [fromString, attendees, handleSelectParticipants, poll, onCalendarTimezoneSelected,
    displayedParticipantsWithoutActive, convertedSlots, handleCheckSlot, selectedSlots, calendarTimezoneSelected,
    hidePanel, name, description, durationText, numRespondedParticipants, userData, date, onDateChange,
    calendarOpen, noTimesHover, handleSubmitTimes, showCalendarButton, handleViewCalendar, isAuthenticated,
    answers, handleEditSelections, step, handleQuestionFormStateChange, handleAttendeeFormStateChange,
    handleOpenSlotNoteModal, handleRemoveSlotNote, participantAttendees, selectableVoters, attendeeListEnabled]);
  
  // const slotsAreSelected = selectedCount > 0;

  // const showMeetingInfoPanel = !hidePanel && !calendarOpen;
  
  if (loading) return null;
  
  return (
    <Box sx={{ height: '100vh', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {isMdDown && logo && (
        <Box
          component='img'
          src={logo}
          alt='Cabinet'
          sx={{
            width: 180,
            marginTop: 3
          }} />
      )}
      <BookMeetingWrapper
        showSidePanel={!hidePanel && !calendarOpen}
        showCabinetLogoInSidePanel={true}
        logo={logo || organization.logo || undefined}
        loadingPage={loading}
        meetingInfo={poll || {} as ExternalMeetingInfo}
        meetingTitle={name}
        meetingDescription={description}
        durationText={durationText}
        isAuthenticated={isAuthenticated}
        templateVars={{}}
        sx={{ minWidth: isMdDown ? 300 : 400, width: '100%', height: '100%' }}
        header={<></>}
        step={step as unknown as MeetingBookingStep}
        showBackButton={step > PollStep.PARTICIPANT_INFO && step !== PollStep.CONFIRMATION}
        showSubmitButton={step !== PollStep.CONFIRMATION}
        onSubmit={formSubmit}
        hideFooter={step === PollStep.PARTICIPANT_INFO || step === PollStep.CONFIRMATION}
        hideTitleDescription={hidePanel}
        stickyFooter
        submitLabel={step === PollStep.PARTICIPANT_INFO
          || (step === PollStep.QUESTIONS && questionsPresentForSelectedAttendees)
          ? 'Continue' : 'Confirm Times'}
        submitDisabled={
          !isStepValid || saving || (step === PollStep.SELECT_TIMES && hasLeaderAttendees && !selectedCount)
        }
        submitLoading={saving}
        onBackButtonClick={() => setStep(step - 1)}
      >
        {convertedSlots.length && poll?.status.id === MeetingStatus.PENDING ? (
          <>
            <Box sx={{
              padding: isMdDown ? 2 : 4
            }}>
              {Step}
              {step === PollStep.SELECT_TIMES && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', mr: 7 }}>
                  <CabButton
                    buttonType="text"
                    onClick={() => setOpenNoTimesModal(true)}
                    disabled={selectedCount > 0 || noTimesDisabled || hasLeaderAttendees}
                    sx={{ 
                      whiteSpace: 'nowrap',
                      textDecoration: 'underline',
                      color: colors.calmGrey600
                    }}
                  >
                    None of these times work
                  </CabButton>
                </Box>
              )}
            </Box>
            {step === PollStep.CONFIRMATION && (
              <div>
                <PollResponseCTA
                  isAuthenticated={isAuthenticated}
                  event={EVENT_TYPE.MEETING_POLL_SIGNUP}
                  organizationId={organization.id}
                  organizationName={organization.name}
                  meetingId={poll.id}
                  userId={poll.create_user.id}
                />
              </div>
            )}
          </>
        ) : (
          <Box>
            { poll && poll?.status.id !== MeetingStatus.PENDING ? (
              <Alert severity='warning'>
                This poll is no longer accepting votes. You can email the organizer, 
                {poll.create_user.first_name}&nbsp; 
                {poll.create_user.last_name}, at <b>{poll.create_user.email}</b>.
              </Alert>
            ) : (
              <Alert severity='warning'>
                There are currently no time slots available for this poll.
              </Alert>
            )}
          </Box>
        )}
      </BookMeetingWrapper>
      {poll?.id && hidePanel && isAuthenticated && !isMdDown && (
        <Box
          sx={{
            display: 'flex',
            padding: 2,
            backgroundColor: colors.greyBackdrop
          }}>
          <Typography variant="body2">Are you the meeting owner? &nbsp;</Typography>
          <Link component={RouterLink} to={`${PAGE_URL.SCHEDULE}/${poll.id}`}
            variant="body2" color={colors.black900} sx={{
              fontWeight: 500
            }}
          >
            Click here
          </Link>
          <Typography variant="body2">&nbsp;to make changes.</Typography>
        </Box>
      )}
      <CabModal 
        open={openNoTimesModal}
        closeIcon={true}
        onClose={() => setOpenNoTimesModal(false)}
        title="Send a comment"
      >
        <Box>
          <Typography sx={{marginBottom: 2}}>
            We'll let {poll?.create_user.first_name} know that none of these times work for you. 
            Use the field below to suggest alternative times or provide additional information.
          </Typography>
          <form onSubmit={(e) => handleSubmit((data) => submitComments(data, selectedCount))(e)}>
            {attendees.map((attendee, idx) => (
              <Controller
                key={attendee.email}
                control={control}
                name={`${idx}`}
                render={({field: {ref, ...field}}) => (
                  <FormControl
                    key={attendee.email}
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    <CabTextInput
                      multiline
                      minRows={4}
                      label={attendees.length > 1 ? attendee.name : undefined}
                      {...field}
                    />
                  </FormControl>
                )}
              />
            ))}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row-reverse",
                width: "100%",
                marginTop: 2
              }}>
              <CabButton type="submit" sx={{marginLeft: 1}}>Submit</CabButton>
              <CabButton buttonType="secondary" onClick={() => {
                reset(Array(attendees.length).fill(null));
                setOpenNoTimesModal(false);
              }}>Cancel</CabButton>
            </Box>
          </form>
        </Box>
      </CabModal>
      <CabModal
        open={openSlotNoteModal}
        closeIcon={true}
        onClose={() => setOpenSlotNoteModal(false)}
        title={slotNoteModalMode === "edit" ? "Edit Meeting Note" : "Add Meeting Note"}
        text="Include a note on this time slot which will be viewable to the individual booking the meeting"
        actionButtons={
          <>
            <CabButton
              buttonType="secondary"
              onClick={() => setOpenSlotNoteModal(false)}
            >
              Cancel
            </CabButton>
            <CabButton
              onClick={handleSaveSlotNote}
            >
              Save Note
            </CabButton>
          </>
        }
      >
        <Box>
          <Box sx={{ marginTop: 3, marginBottom: 2 }}>
            {slotNoteModalSlot ? (
              <Box
                sx={{
                  display: "flex",
                  gap: 1
                }}>
                <CabIcon Icon={IoTimeOutline} sx={{ fontSize: 22 }} />
                <Typography>
                  Time Slot:{" "}
                  <b>
                    {slotNoteModalSlot.start.toLocaleString({
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                    })}
                  </b>
                  {!poll?.all_day_slots && (
                    <>
                      {" "}from{" "}
                      <b>{slotNoteModalSlot.start.toFormat("h:mma").toLowerCase()}</b> -{" "}
                      <b>{slotNoteModalSlot.end.toFormat("h:mma").toLowerCase()}</b>
                    </>
                  )}
                  .
                </Typography>
              </Box>
            ) : null}
          </Box>
          <CabTextInput
            multiline
            minRows={4}
            placeholder="Add optional time slot note..."
            value={slotNoteText}
            onChange={(e) => setSlotNoteText(e.target.value)}
            fullWidth
          />
        </Box>
      </CabModal>
    </Box>
  );
};
